<script setup>
import { ref, reactive, computed, onMounted, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { useMetadataListasStore } from '@/stores/metadataListas'

const emit = defineEmits(['adicionar-item', 'editar-item'])

const authStore = useAuthStore()
const { obterClasseStatusGhost } = useUI()
const metadataStore = useMetadataListasStore()

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(10)

const planos = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  statusPlanos: []
})

const filtros = reactive({
  nome: '',
  descricao: '',
  lstStatus: '',
  validadeInicioDe: '',
  validadeInicioAte: '',
  validadeFimDe: '',
  validadeFimAte: ''
})

// --- Estado da Auditoria ---
const mostrarAuditoria = ref(false)
const planoSelcionadoParaAuditoria = ref(null)

const temPermissaoCompleta = computed(() => authStore.pode('PLANOS_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('PLANOS_VISUALIZACAO'))

const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idPlano', label: 'ID', width: 100, sortable: true, slotName: 'id' },
    { field: 'nome', label: 'Nome do Plano', sortable: true, class: 'fw-medium' },
    { field: 'descricao', label: 'Descrição', sortable: true },
    { field: 'lstStatus', label: 'Status', sortable: true, width: 120, slotName: 'lstStatus' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 150, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (filtros.nome) params.append('nome', filtros.nome)
    if (filtros.descricao) params.append('descricao', filtros.descricao)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (filtros.validadeInicioDe) params.append('validadeInicioDe', filtros.validadeInicioDe)
    if (filtros.validadeInicioAte) params.append('validadeInicioAte', filtros.validadeInicioAte)
    if (filtros.validadeFimDe) params.append('validadeFimDe', filtros.validadeFimDe)
    if (filtros.validadeFimAte) params.append('validadeFimAte', filtros.validadeFimAte)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/Planos?${params.toString()}`)
    planos.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar planos:', error)
    erro.value = 'Não foi possível carregar os dados dos planos.'
    planos.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => {
  filtros.nome = ''
  filtros.descricao = ''
  filtros.lstStatus = ''
  filtros.validadeInicioDe = ''
  filtros.validadeInicioAte = ''
  filtros.validadeFimDe = ''
  filtros.validadeFimAte = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarPlano = () => { emit('adicionar-item') }
const editarPlano = (plano) => { emit('editar-item', plano) }

const abrirAuditoria = (plano) => {
  planoSelcionadoParaAuditoria.value = plano
  mostrarAuditoria.value = true
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.statusPlanos.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

const carregarListas = async () => {
  try {
    const res = await metadataStore.getListaLookup('ST', 'Sistema')
    listas.statusPlanos = res || []
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

onMounted(async () => {
  await carregarListas()
  buscarDados(1)
})
onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Planos"
    subtitulo="Listagem e gerenciamento de registos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarPlano"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-nome" class="form-label">Nome do Plano</label>
          <input type="text" id="filtro-nome" class="form-control" v-model="filtros.nome" placeholder="Digite para buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label for="filtro-descricao" class="form-label">Descrição</label>
          <input type="text" id="filtro-descricao" class="form-control" v-model="filtros.descricao" placeholder="Buscar na descrição..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="filtro-lstStatus"
            label="Status"
            v-model="filtros.lstStatus"
            :options="listas.statusPlanos || []"
            option-value="idListaSistemaItem"
            option-label="textoLista"
          />
        </div>

        <div class="col-md-4">
          <CampoDataIntervalo
            id="filtro-validade-inicio"
            label="Validade Início"
            v-model:model-value-inicio="filtros.validadeInicioDe"
            v-model:model-value-fim="filtros.validadeInicioAte"
          />
        </div>
        <div class="col-md-4">
          <CampoDataIntervalo
            id="filtro-validade-fim"
            label="Validade Fim"
            v-model:model-value-inicio="filtros.validadeFimDe"
            v-model:model-value-fim="filtros.validadeFimAte"
          />
        </div>

        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="planos"
      :loading="loading"
      empty-message="Nenhum registo encontrado."
      row-key="idPlano"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarPlano($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idPlano }}</span>
      </template>
      <template #lstStatus="{ row }">
        <span :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-primary" title="Editar" @click="editarPlano(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <button class="btn-icon text-secondary" title="Ver Histórico" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria && planoSelcionadoParaAuditoria"
    tabela="Plano"
    contexto="master"
    :chave-primaria="planoSelcionadoParaAuditoria.idPlano"
    :titulo-registro="planoSelcionadoParaAuditoria.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
